import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Chip } from "@zorni/components-lib";

import styles from "./UpcomingTripCard.module.css";

const formatDay = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
};

/** Jours restants avant le départ, à partir d'aujourd'hui. */
export const daysUntil = (value) => {
  if (!value) return null;
  const start = new Date(value);
  if (Number.isNaN(start.getTime())) return null;
  const today = new Date();
  const diff = Math.ceil((start.setHours(0, 0, 0, 0) - today.setHours(0, 0, 0, 0)) / 86400000);
  return diff > 0 ? diff : null;
};

/**
 * Carte d'un voyage à venir — « 07 - mes voyages ».
 * Photo à gauche, statut, repères (nuits, voyageurs, montant), dates et
 * compte à rebours, puis les deux actions.
 */
const UpcomingTripCard = ({
  reference,
  picture,
  title,
  location,
  status,
  nights,
  travelers,
  price,
  startDate,
  endDate,
  onSeeReservation,
  onContactAgency,
}) => {
  const { t } = useTranslation();
  const confirmed = status === "CONFIRMED";
  const days = daysUntil(startDate);

  return (
    <article className={styles.card}>
      <div className={styles.media}>
        {picture ? <img src={picture} alt="" loading="lazy" /> : <span className={styles.noPhoto} />}
        {reference && <span className={styles.ref}>{t("myTrips.reference", { reference, defaultValue: `Réf. ${reference}` })}</span>}
      </div>

      <div className={styles.body}>
        <header className={styles.head}>
          <div>
            <h3>{title}</h3>
            {location && <p className={styles.location}>{location}</p>}
          </div>
          <span className={`${styles.badge} ${confirmed ? styles.ok : styles.wait}`}>
            {confirmed
              ? t("myTrips.status.confirmed", "Confirmé")
              : t("myTrips.status.pending", "En attente")}
          </span>
        </header>

        <div className={styles.chips}>
          {nights > 0 && (
            <Chip
              variant="meta"
              iconName="iconNights"
              label={t("myTrips.nights", { count: nights, defaultValue: `${nights} nuits` })}
            />
          )}
          {travelers > 0 && (
            <Chip
              variant="meta"
              iconName="iconUsers"
              label={t("myTrips.travelers", { count: travelers, defaultValue: `${travelers} voyageurs` })}
            />
          )}
          {price && <Chip variant="meta" iconName="iconPriceQuality" label={price} />}
        </div>

        <p className={styles.when}>
          <span className={styles.dates}>
            {formatDay(startDate)} → {formatDay(endDate)}
          </span>
          {days !== null && (
            <span className={styles.countdown}>
              {t("myTrips.countdown", { count: days, defaultValue: `Départ dans ${days} jours` })}
            </span>
          )}
        </p>

        <div className={styles.actions}>
          <Button
            variant="primary"
            size="medium"
            iconName="iconArrowRight"
            iconPosition="right"
            label={t("myTrips.seeReservation", "Voir ma réservation")}
            onClick={onSeeReservation}
          />
          <Button
            variant="secondary"
            size="medium"
            iconName="iconEnvelope"
            iconPosition="left"
            label={t("myTrips.contactAgency", "Contacter l'agence")}
            onClick={onContactAgency}
          />
        </div>
      </div>
    </article>
  );
};

UpcomingTripCard.propTypes = {
  reference: PropTypes.string,
  picture: PropTypes.string,
  title: PropTypes.string,
  location: PropTypes.string,
  status: PropTypes.string,
  nights: PropTypes.number,
  travelers: PropTypes.number,
  price: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  onSeeReservation: PropTypes.func,
  onContactAgency: PropTypes.func,
};

export default UpcomingTripCard;
